import React, { useState, useEffect } from 'react';
import API_BASE from '../../utils/api.js';
import notify from '../../utils/notify';

const ACTIVITY_PRESETS = ['Meeting', 'Procurement', 'Sourcing', 'Design'];

function normalizeRoleKey(value) {
  return String(value || '')
    .trim()
    .toLowerCase();
}

function getProjectRoleRateMap(project) {
  const entries = Array.isArray(project?.timeBillingRoleRates) ? project.timeBillingRoleRates : [];

  return entries.reduce((acc, entry) => {
    const roleKey = normalizeRoleKey(entry?.role);
    const rate = Number(entry?.rate);
    if (!roleKey || !Number.isFinite(rate)) return acc;
    acc[roleKey] = rate;
    return acc;
  }, {});
}

export default function TimeBillingForm({ projectId, project }) {
  const apiUrl = API_BASE;
  const userId = localStorage.getItem('userId');

  /* ================================================
     UI + Mode State
  ================================================= */
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState('create');
  const [editTarget, setEditTarget] = useState(null);

  /* ================================================
     Team Settings
  ================================================= */
  const [team, setTeam] = useState([]);
  const [activities, setActivities] = useState(ACTIVITY_PRESETS);
  const [projectRoleRates, setProjectRoleRates] = useState({});

  /* ================================================
     Form State
  ================================================= */
  const [form, setForm] = useState({
    date: '',
    employee: '',
    employeeMode: 'preset',
    activity: '',
    activityMode: 'preset',
    billableHours: '',
    employeeRate: '',
    employeeRateTouched: false,
    rate: '',
    description: '',
    taxable: true,
    billable: true,
  });

  /* ================================================
     Load TEAM + Stored Activities
  ================================================= */
  useEffect(() => {
    if (!userId) return;

    async function loadSettings() {
      try {
        const res = await fetch(`${apiUrl}/api/settings/${userId}`);
        const data = await res.json();
        const loadedTeam = Array.isArray(data.team) ? data.team : [];
        const storedActivities = Array.isArray(data.timeActivities)
          ? data.timeActivities.map((a) => (typeof a === 'string' ? a.trim() : '')).filter(Boolean)
          : [];

        setTeam(loadedTeam);
        setActivities(Array.from(new Set([...ACTIVITY_PRESETS, ...storedActivities])));
      } catch (err) {
        console.error('Failed to load settings', err);
      }
    }
    loadSettings();
  }, [userId, apiUrl]);

  useEffect(() => {
    setProjectRoleRates(getProjectRoleRateMap(project));
  }, [project]);

  /* ================================================
     Open / Edit Listener
  ================================================= */
  useEffect(() => {
    const handleOpen = (e) => {
      const { mode, entry } = e.detail;

      setMode(mode);
      setOpen(true);

      if (mode === 'edit' && entry) {
        setEditTarget(entry);

        setForm({
          date: entry.date || entry.createdAt?.split('T')[0] || '',
          employee: entry.employee || '',
          employeeMode: 'preset',
          activity: entry.activity || '',
          activityMode: 'preset',
          billableHours: entry.billableHours || '',
          employeeRate: entry.employeeRate ?? '',
          employeeRateTouched: false,
          rate: entry.rate ?? '',
          description: entry.description || '',
          taxable: entry.taxable ?? true,
          billable: entry.billable ?? true,
        });
      } else {
        resetForm();
      }
    };

    document.addEventListener('open-timebilling-form', handleOpen);
    return () => document.removeEventListener('open-timebilling-form', handleOpen);
  }, []);

  const resetForm = () => {
    setForm({
      date: new Date().toISOString().split('T')[0],
      employee: '',
      employeeMode: 'preset',
      activity: '',
      activityMode: 'preset',
      billableHours: '',
      employeeRate: '',
      employeeRateTouched: false,
      rate: '',
      description: '',
      taxable: true,
      billable: true,
    });
    setEditTarget(null);
  };

  const closeModal = () => {
    setOpen(false);
    resetForm();
  };

  /* ================================================
     Employee Selection → auto employeeRate
  ================================================= */
  const handleEmployeeSelect = (value) => {
    if (value === '__manual__') {
      setForm((f) => ({
        ...f,
        employee: '',
        employeeMode: 'manual',
        employeeRate: '',
        employeeRateTouched: false,
        rate: '',
      }));
      return;
    }

    const emp = team.find((t) => t.name === value);
    const costRate = emp?.cost ? Number(emp.cost) : '';
    const roleKey = normalizeRoleKey(emp?.role);
    const clientRate =
      roleKey && projectRoleRates[roleKey] != null ? projectRoleRates[roleKey] : '';

    setForm((f) => ({
      ...f,
      employee: value,
      employeeMode: 'preset',
      employeeRate: costRate,
      employeeRateTouched: false,
      rate: clientRate,
    }));
  };

  const handleActivitySelect = (value) => {
    if (value === '__custom__') {
      setForm((f) => ({ ...f, activity: '', activityMode: 'custom' }));
      return;
    }

    setForm((f) => ({ ...f, activity: value, activityMode: 'preset' }));
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((f) => ({
      ...f,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  /* ================================================
     Submit (FIXED)
  ================================================= */
  const handleSubmit = async (e) => {
    e.preventDefault();

    const employeeValue = String(form.employee || '').trim();
    const activityValue = String(form.activity || '').trim();

    if (!employeeValue || !activityValue) {
      notify('Please select or enter both employee and activity.');
      return;
    }

    const hours = Number(form.billableHours || 0);
    const clientRate = Number(form.rate || 0);

    let employeeRate;

    if (mode === 'create') {
      employeeRate = form.employeeRateTouched
        ? Number(form.employeeRate || 0)
        : form.employeeRate !== ''
          ? Number(form.employeeRate)
          : clientRate;
    } else {
      // EDIT MODE — never auto-derive
      employeeRate = Number(form.employeeRate || 0);
    }

    const payload = {
      date: form.date || new Date().toISOString().split('T')[0],
      employee: employeeValue,
      activity: activityValue,
      billableHours: hours,
      rate: clientRate,
      employeeRate,
      description: form.description,
      taxable: form.taxable,
      billable: form.billable,
    };

    try {
      let res;

      if (mode === 'create') {
        res = await fetch(`${apiUrl}/api/timebilling/${userId}/${projectId}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });
      } else {
        res = await fetch(`${apiUrl}/api/timebilling/${userId}/${editTarget.sortKey}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });
      }

      if (!res.ok) {
        notify('Failed to save entry');
        return;
      }

      if (mode === 'create') {
        let nextTeam = team;
        let nextActivities = activities;
        const settingsUpdates = {};

        if (
          form.employeeMode === 'manual' &&
          !team.some(
            (t) =>
              String(t?.name || '')
                .trim()
                .toLowerCase() === employeeValue.toLowerCase()
          )
        ) {
          const today = new Date().toISOString().split('T')[0];
          nextTeam = [
            ...team,
            {
              name: employeeValue,
              role: 'Team Member',
              cost: Number(employeeRate || 0),
              date: today,
              email: '',
              phone: '',
              street: '',
              city: '',
              state: '',
              zip: '',
            },
          ];
          setTeam(nextTeam);
          settingsUpdates.team = nextTeam;
        }

        if (
          form.activityMode === 'custom' &&
          !activities.some((a) => String(a).trim().toLowerCase() === activityValue.toLowerCase())
        ) {
          nextActivities = [...activities, activityValue];
          setActivities(nextActivities);
          settingsUpdates.timeActivities = nextActivities;
        }

        if (Object.keys(settingsUpdates).length > 0) {
          try {
            await fetch(`${apiUrl}/api/settings/${userId}`, {
              method: 'PUT',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify(settingsUpdates),
            });
          } catch (settingsErr) {
            console.error('Saved entry but failed to persist team/activity settings:', settingsErr);
          }
        }
      }

      document.dispatchEvent(new Event('timebilling-updated'));
      closeModal();
    } catch (err) {
      console.error('Failed to save entry:', err);
    }
  };

  if (!open) return null;

  return (
    <div className="form-backdrop" onClick={closeModal}>
      <div className="form-modal" onClick={(e) => e.stopPropagation()}>
        <h3>{mode === 'create' ? 'New Time Entry' : 'Edit Time Entry'}</h3>

        <form onSubmit={handleSubmit}>
          <input name="date" type="date" value={form.date} onChange={handleChange} />

          {/* EMPLOYEE */}
          {form.employeeMode !== 'manual' ? (
            <div className="tb-select-wrap">
              <select
                className="tb-form-select"
                value={form.employee}
                onChange={(e) => handleEmployeeSelect(e.target.value)}
              >
                <option value="">Select Employee</option>
                {team.map((t, i) => (
                  <option key={i} value={t.name}>
                    {t.name}
                  </option>
                ))}
                <option value="__manual__">+ Add New Employee</option>
              </select>
            </div>
          ) : (
            <input
              name="employee"
              placeholder="Employee Name"
              value={form.employee}
              onChange={handleChange}
            />
          )}

          {/* ACTIVITY */}
          {form.activityMode !== 'custom' ? (
            <div className="tb-select-wrap">
              <select
                className="tb-form-select"
                value={form.activity}
                onChange={(e) => handleActivitySelect(e.target.value)}
              >
                <option value="">Select Activity</option>
                {activities.map((a, i) => (
                  <option key={i} value={a}>
                    {a}
                  </option>
                ))}
                <option value="__custom__">+ Custom Activity</option>
              </select>
            </div>
          ) : (
            <input
              name="activity"
              placeholder="Enter custom activity"
              value={form.activity}
              onChange={handleChange}
            />
          )}

          <input
            name="billableHours"
            type="number"
            step="0.01"
            placeholder="Hours"
            value={form.billableHours}
            onChange={handleChange}
          />

          <input
            name="employeeRate"
            type="number"
            step="0.01"
            placeholder="Employee Rate (Cost)"
            value={form.employeeRate}
            onChange={(e) =>
              setForm((f) => ({
                ...f,
                employeeRate: e.target.value,
                employeeRateTouched: true,
              }))
            }
          />

          <input
            name="rate"
            type="number"
            step="0.01"
            placeholder="Client Rate"
            value={form.rate}
            onChange={handleChange}
          />

          <textarea
            name="description"
            placeholder="Description"
            value={form.description}
            onChange={handleChange}
          />

          <label className="checkbox-row">
            <input type="checkbox" name="taxable" checked={form.taxable} onChange={handleChange} />
            Taxable?
          </label>

          <label className="checkbox-row">
            <input type="checkbox" name="billable" checked={form.billable} onChange={handleChange} />
            Billable to client?
          </label>

          <div className="form-actions">
            <button type="button" onClick={closeModal}>
              Cancel
            </button>
            <button type="submit">{mode === 'create' ? 'Add' : 'Save'}</button>
          </div>
        </form>
      </div>
    </div>
  );
}
